/*
 * Provisioning in the core API: prov.*, settings.*, directory.get, with the
 * events of the native core (mt-vox docs/api.md, "Provisioning").
 *
 *   off -> pairing (code shown, polled) -> enrolling -> active
 *
 * Active: GET /v1/config every poll_interval (ETag), lines reconciled with
 * account.add / account.remove, the company directory refetched when its
 * revision moves. A revoked or replaced device forgets everything and pairs
 * again. Lines marked "auth": "ephemeral" register with short-lived
 * credentials from GET /v1/sip-credentials, renewed before they expire.
 */
import type { Core, CoreModule } from "../core/core";
import { CommandError, Command, Json } from "../core/types";
import {
  Credential,
  DeviceInfo,
  Directory,
  ProvAccount,
  ProvClient,
  ProvConfig,
  ProvError,
  SettingEntry,
  SipCredentials,
} from "./client";
import type { CallLogModule } from "./calllog";
import type { ChatModule } from "./chat";
import type { ContactsModule } from "./contacts";
import type { KV } from "./store";

export type ProvState = "off" | "pairing" | "enrolling" | "active";

const REVOKED = new Set(["device_revoked", "device_replaced", "invalid_credentials"]);
const RETRY_MS = 5_000;

interface Timers {
  set(fn: () => void, ms: number): unknown;
  clear(t: unknown): void;
  now(): number;
}

const realTimers: Timers = {
  set: (fn, ms) => setTimeout(fn, ms),
  clear: (t) => clearTimeout(t as ReturnType<typeof setTimeout>),
  now: () => Date.now(),
};

interface Applied {
  sig: string;
  account: string;
}

export class ProvModule implements CoreModule {
  private state: ProvState = "off";
  private client?: ProvClient;
  private device?: DeviceInfo;
  private cred?: Credential;
  private config?: ProvConfig;
  private directory?: Directory;
  private pairing?: { id: string; code: string; expiresAt: number };
  private timer?: unknown;
  private credTimer?: unknown;
  private turnTimer?: unknown;
  private turnUntil = 0; // ms: the TURN credentials in use are valid until then (0: none, -1: no TURN server)
  private applied = new Map<string, Applied>(); // line_id -> account
  private sip?: SipCredentials;
  private generation = 0; // bumps on stop/reset: stale async work stops
  private contacts?: ContactsModule;
  private chat?: ChatModule;
  private callLog?: CallLogModule;

  constructor(
    private core: Core,
    private kv: KV,
    private clientFor: (url: string) => ProvClient = (u) => new ProvClient(u),
    private timers: Timers = realTimers,
  ) {}

  /** The address book syncs through this module's identity (contacts.ts). */
  attachContacts(m: ContactsModule): void {
    this.contacts = m;
  }

  /** Chat between users syncs through this module's identity too (chat.ts). */
  attachChat(m: ChatModule): void {
    this.chat = m;
  }

  /** The shared call list too (calllog.ts). */
  attachCallLog(m: CallLogModule): void {
    this.callLog = m;
  }

  command(c: Command): Promise<Json | void> | undefined {
    switch (c.cmd) {
      case "prov.start":
        return this.start(String(c.url ?? ""), c.device as DeviceInfo);
      case "prov.enroll":
        return this.enrollToken(String(c.token ?? ""));
      case "prov.sync":
        return this.sync();
      case "prov.reset":
        return this.reset();
      case "prov.stop":
        return Promise.resolve(this.halt());
      case "prov.status":
        return Promise.resolve(this.status());
      case "settings.list":
        return Promise.resolve({ settings: this.config?.settings ?? {} });
      case "settings.set":
        return this.setSetting(c);
      case "directory.get":
        return Promise.resolve(this.directory ? { ...this.directory } : {});
      default:
        return undefined;
    }
  }

  stop(): void {
    this.halt();
  }

  /** The server and the device credential while active (voicemail module). */
  session(): { client: ProvClient; cred: Credential } | null {
    return this.state === "active" && this.client && this.cred ? { client: this.client, cred: this.cred } : null;
  }

  // --- lifecycle ------------------------------------------------------
  private async start(url: string, device: DeviceInfo): Promise<Json> {
    if (!/^https:\/\/|^http:\/\/(127\.0\.0\.1|localhost|\[::1\])(:\d+)?\//.test(url + "/")) {
      throw new CommandError("invalid", "https, or http on loopback only");
    }
    if (!device || typeof device !== "object") throw new CommandError("invalid", "device");
    this.halt();
    const gen = ++this.generation;
    this.client = this.clientFor(url);
    this.device = { ...device, platform: "web" };
    const saved = await this.kv.get<Credential & { url: string }>("prov.cred");
    if (saved && saved.url === this.client.base) {
      this.cred = saved;
      this.config = await this.kv.get<ProvConfig>("prov.config");
      this.directory = await this.kv.get<Directory>("prov.directory");
      this.setState("active");
      if (this.config) {
        // offline start: the cached lines register right away
        this.emitConfig();
        if (this.directory) this.core.event("directory.changed", { ...this.directory });
        void this.applyAccounts(gen);
      }
      void this.sync();
    } else {
      void this.pair(gen);
    }
    return this.status();
  }

  private halt(): void {
    this.generation++;
    if (this.timer !== undefined) this.timers.clear(this.timer);
    if (this.credTimer !== undefined) this.timers.clear(this.credTimer);
    if (this.turnTimer !== undefined) this.timers.clear(this.turnTimer);
    this.timer = this.credTimer = this.turnTimer = undefined;
    this.turnUntil = 0;
  }

  private async reset(): Promise<void> {
    this.halt();
    await this.forget();
    if (this.client && this.device) void this.pair(this.generation);
  }

  private async forget(): Promise<void> {
    for (const [, a] of this.applied) await this.core.command({ cmd: "account.remove", account: a.account });
    this.applied.clear();
    this.cred = this.config = this.directory = this.sip = undefined;
    await this.kv.del("prov.cred");
    await this.kv.del("prov.config");
    await this.kv.del("prov.directory");
    this.core.event("settings.changed", { settings: {} });
    this.core.event("directory.changed", {});
    await this.contacts?.forget();
    await this.chat?.forget();
    await this.callLog?.forget();
  }

  private setState(s: ProvState): void {
    this.state = s;
    this.core.event("prov.state", {
      state: s,
      ...(this.cred ? { device_id: this.cred.device_id, endpoint_id: this.cred.endpoint_id } : {}),
    });
  }

  private status(): Json {
    return {
      state: this.state,
      url: this.client?.base,
      device_id: this.cred?.device_id,
      endpoint_id: this.cred?.endpoint_id,
      code: this.pairing?.code,
      revision: this.config?.revision,
      accounts: this.config?.accounts.length ?? 0,
    };
  }

  private later(gen: number, ms: number, fn: () => void): void {
    if (gen !== this.generation) return;
    if (this.timer !== undefined) this.timers.clear(this.timer);
    this.timer = this.timers.set(() => {
      if (gen === this.generation) fn();
    }, ms);
  }

  private error(op: string, e: unknown): void {
    const p = e instanceof ProvError ? e : new ProvError(0, "failed", String(e));
    this.core.event("prov.error", { op, status: p.status, code: p.code, message: p.message });
  }

  // --- pairing --------------------------------------------------------
  private async pair(gen: number): Promise<void> {
    if (gen !== this.generation || !this.client || !this.device) return;
    this.setState("pairing");
    try {
      const p = await this.client.startPairing(this.device);
      if (gen !== this.generation) return;
      this.pairing = { id: p.pairing_id, code: p.code, expiresAt: this.timers.now() + p.expires_in * 1000 };
      this.core.event("prov.pairing", { code: p.code, expires_in: p.expires_in });
      this.later(gen, p.poll_interval * 1000, () => void this.pollPairing(gen, p.poll_interval));
    } catch (e) {
      this.error("pairing", e);
      this.later(gen, RETRY_MS, () => void this.pair(gen));
    }
  }

  private async pollPairing(gen: number, every: number): Promise<void> {
    if (!this.client || !this.pairing) return;
    try {
      const r = await this.client.pollPairing(this.pairing.id);
      if (gen !== this.generation) return;
      if (r.status === "claimed") return void this.enroll(gen, { pairing_id: this.pairing.id });
      this.later(gen, every * 1000, () => void this.pollPairing(gen, every));
    } catch (e) {
      if (e instanceof ProvError && (e.status === 410 || e.status === 404)) {
        return void this.pair(gen); // expired: a new code
      }
      this.error("pairing", e);
      this.later(gen, RETRY_MS, () => void this.pollPairing(gen, every));
    }
  }

  private async enrollToken(token: string): Promise<void> {
    if (!this.client || !this.device) throw new CommandError("invalid", "prov.start first");
    if (!/^[0-9A-Za-z -]{12,20}$/.test(token)) throw new CommandError("invalid", "token");
    this.halt();
    await this.enroll(this.generation, { token });
  }

  private async enroll(gen: number, by: { pairing_id: string } | { token: string }): Promise<void> {
    if (!this.client || !this.device) return;
    this.setState("enrolling");
    try {
      const cred = await this.client.enroll(by, this.device);
      if (gen !== this.generation) return;
      this.cred = cred;
      this.pairing = undefined;
      await this.kv.set("prov.cred", { ...cred, url: this.client.base });
      this.setState("active");
      await this.sync();
    } catch (e) {
      this.error("enroll", e);
      // a refused token goes back to the code; a claimed code that fails
      // to enroll starts over too
      if (gen === this.generation) void this.pair(gen);
    }
  }

  // --- config ---------------------------------------------------------
  private async sync(): Promise<void> {
    const gen = this.generation;
    if (!this.client || !this.cred) return;
    try {
      const doc = await this.client.config(this.cred, this.config?.revision);
      if (gen !== this.generation) return;
      if (doc) {
        this.config = doc;
        await this.kv.set("prov.config", doc);
        this.emitConfig();
      }
      await this.applyAccounts(gen);
      await this.syncDirectory(gen);
      if (this.client && this.cred) await this.contacts?.onConfig(this.client, this.cred, this.config?.contacts_seq);
      if (this.client && this.cred) await this.chat?.onConfig(this.client, this.cred, this.config?.chat_seq, this.config?.chat_files === true, this.config?.chat_partners ?? []);
      if (this.client && this.cred) await this.callLog?.onConfig(this.client, this.cred, this.config?.calls_seq);
      this.core.event("prov.config", { revision: this.config?.revision, accounts: this.config?.accounts.length ?? 0 });
      this.later(gen, (this.config?.poll_interval ?? 900) * 1000, () => void this.sync());
    } catch (e) {
      if (gen !== this.generation) return;
      if (e instanceof ProvError && e.status === 401 && REVOKED.has(e.code)) {
        return this.revoked(e.code);
      }
      this.error("config", e);
      this.later(gen, RETRY_MS * 6, () => void this.sync());
    }
  }

  private async revoked(reason: string): Promise<void> {
    this.halt();
    this.core.event("prov.revoked", { reason });
    await this.forget();
    void this.pair(this.generation);
  }

  private emitConfig(): void {
    this.core.event("settings.changed", { settings: this.config?.settings ?? {} });
  }

  private async syncDirectory(gen: number): Promise<void> {
    const want = this.config?.directory_revision ?? null;
    if (want === null) {
      if (this.directory) {
        this.directory = undefined;
        await this.kv.del("prov.directory");
        this.core.event("directory.changed", {});
      }
      return;
    }
    if (this.directory?.revision === want || !this.client || !this.cred) return;
    try {
      const d = await this.client.directory(this.cred, this.directory?.revision);
      if (gen !== this.generation || !d) return;
      this.directory = d;
      await this.kv.set("prov.directory", d);
      this.core.event("directory.changed", { ...d });
    } catch (e) {
      this.error("directory", e);
    }
  }

  // --- lines ----------------------------------------------------------
  private accountId(a: ProvAccount): string {
    return `line-${a.line_id}`;
  }

  private async applyAccounts(gen: number): Promise<void> {
    const accounts = this.config?.accounts ?? [];
    const ephemeral = accounts.some((a) => a.auth === "ephemeral");
    if (ephemeral && !this.sipValid()) {
      try {
        await this.refreshSip(gen);
      } catch (e) {
        this.error("account", e);
        return; // retried with the next sync
      }
    }
    if (gen !== this.generation) return;
    // TURN for the media (the ephemeral SIP credentials carry their own),
    // without holding up the lines' registration
    if (!ephemeral && accounts.length && this.turnUntil >= 0 && this.turnUntil - this.timers.now() < 60_000) {
      void this.refreshTurn(gen).catch((e) => this.error("turn", e));
    }
    const keep = new Set<string>();
    for (const a of accounts) {
      keep.add(a.line_id);
      const sig = JSON.stringify([a.username, a.domain, a.auth_userid, a.ha1 ?? "eph", a.display_name, a.expires, a.line, a.caller_id ?? ""]);
      const cur = this.applied.get(a.line_id);
      if (cur?.sig === sig) continue;
      if (cur) await this.core.command({ cmd: "account.remove", account: cur.account });
      const id = this.accountId(a);
      const secret = a.auth === "ephemeral" ? this.sipFor(a) : { auth_user: a.auth_userid, ha1: a.ha1 };
      if (!secret) continue;
      const r = await this.core.command({
        cmd: "account.add",
        account: {
          id,
          uri: `sip:${a.username}@${a.domain}`,
          display_name: a.display_name,
          line: a.line,
          ...(a.caller_id ? { caller_id: a.caller_id } : {}),
          expires: a.expires,
          ...secret,
        },
      });
      if (r.ok) this.applied.set(a.line_id, { sig, account: id });
      else this.error("account", new ProvError(0, String(r.error), String(r.message ?? "")));
    }
    for (const [line, a] of this.applied) {
      if (!keep.has(line)) {
        await this.core.command({ cmd: "account.remove", account: a.account });
        this.applied.delete(line);
      }
    }
  }

  private sipValid(): boolean {
    return !!this.sip && this.sip.expires_at * 1000 - this.timers.now() > 60_000;
  }

  private sipFor(a: ProvAccount): { auth_user: string; password: string } | undefined {
    const c = this.sip?.accounts.find((x) => x.line_id === a.line_id);
    return c ? { auth_user: c.auth_userid, password: c.password } : undefined;
  }

  private async refreshSip(gen: number): Promise<void> {
    if (!this.client || !this.cred) return;
    const sip = await this.client.sipCredentials(this.cred);
    if (gen !== this.generation) return;
    this.sip = sip;
    if (sip.ice_servers) await this.core.command({ cmd: "core.ice_servers", ice_servers: sip.ice_servers });
    // lines already registered take the new credentials at their next challenge
    for (const [line, a] of this.applied) {
      const c = sip.accounts.find((x) => x.line_id === line);
      if (c) await this.core.command({ cmd: "account.credentials", account: a.account, auth_user: c.auth_userid, password: c.password });
    }
    const renewIn = Math.max(30_000, (sip.expires_at * 1000 - this.timers.now()) * 0.8);
    if (this.credTimer !== undefined) this.timers.clear(this.credTimer);
    this.credTimer = this.timers.set(() => {
      if (gen !== this.generation) return;
      this.refreshSip(gen).catch((e) => {
        this.error("account", e);
        this.credTimer = this.timers.set(() => void this.refreshSip(gen).catch(() => undefined), RETRY_MS * 6);
      });
    }, renewIn);
  }

  // The browser's TURN credentials (mt-prov-sf GET /v1/ice-servers), renewed
  // before they expire. None from the server (404): STUN from config.json
  // only, asked again at the next start.
  private async refreshTurn(gen: number): Promise<void> {
    if (!this.client || !this.cred) return;
    let doc;
    try {
      doc = await this.client.iceServers(this.cred);
    } catch (e) {
      if (e instanceof ProvError && e.status === 404) {
        this.turnUntil = -1;
        return;
      }
      throw e;
    }
    if (gen !== this.generation) return;
    await this.core.command({ cmd: "core.ice_servers", ice_servers: doc.ice_servers });
    this.turnUntil = doc.expires_at * 1000;
    const renewIn = Math.max(30_000, (doc.expires_at * 1000 - this.timers.now()) * 0.8);
    if (this.turnTimer !== undefined) this.timers.clear(this.turnTimer);
    this.turnTimer = this.timers.set(() => {
      if (gen !== this.generation) return;
      this.refreshTurn(gen).catch((e) => {
        this.error("turn", e);
        this.turnTimer = this.timers.set(() => void this.refreshTurn(gen).catch(() => undefined), RETRY_MS * 6);
      });
    }, renewIn);
  }

  // --- settings -------------------------------------------------------
  private async setSetting(c: Command): Promise<void> {
    const key = String(c.key ?? "");
    const entry: SettingEntry | undefined = this.config?.settings[key];
    if (!entry) throw new CommandError("unknown_key");
    if (!entry.edit) throw new CommandError(entry.lock ? "locked" : "not_editable");
    const reset = c.reset === true;
    if (!reset && !("value" in c)) throw new CommandError("invalid");
    if (!reset) {
      this.config!.settings[key] = { ...entry, v: c.value, src: "user" };
      this.emitConfig();
    }
    if (!this.client || !this.cred) return;
    const change = reset
      ? { key, reset: true, changed_at: Math.floor(this.timers.now() / 1000) }
      : { key, value: c.value, changed_at: Math.floor(this.timers.now() / 1000) };
    try {
      const r = await this.client.patchSettings(this.cred, this.config?.revision, [change]);
      for (const x of r.rejected) {
        this.config!.settings[x.key] = x.effective;
        this.core.event("settings.rejected", { key: x.key, code: x.code, effective: x.effective });
      }
      if (r.rejected.length) this.emitConfig();
    } catch (e) {
      this.error("settings", e);
    }
    await this.sync();
  }
}
